<template>
  <div class="loading-screen">
    <InlineLoader />
    <div class="captcha-container">
      <guest-recaptcha
        v-if="$route.name === 'DemoPresentation'"
        @success="onRecaptchaSuccess"
        @error="onRecaptchaError"
        ref="recaptcha"
        @offline="onOffline"
      />
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapState, mapActions } from 'vuex'
import constants from '@/constant'
import Utils from '@/util'
import cookie from '@/services/cookie'

// api
import UserApi from '@/api/user'
import PresentationApi from '@/api/presentation'
import InlineLoader from '@/components-v2/common/loading/InlineLoader.vue'

import gaCategories from '@/constant/gaCategories'
import GuestRecaptcha from './GuestRecaptcha.vue'

export default {
  components: {
    GuestRecaptcha,
    InlineLoader,
  },
  data() {
    return {
      sourcePresentationId: null,
      refer: null,
      fromButton: null,
      tokenRecaptcha: null,
    }
  },
  computed: {
    ...mapState({
      isGuestUser: (state) => state.currentUser.isGuest,
    }),
  },
  mounted() {
    this.sourcePresentationId = Utils.getParamValue('sourcePresentation', window.location.href)
    this.refer = Utils.getParamValue('refer', window.location.href)
    this.fromButton = Utils.getParamValue('from-button', window.location.href)
    if (this.sourcePresentationId) {
      setTimeout(() => { // setTimeout to make sure recaptcha script is loadded
        this.$refs.recaptcha.execute()
        Utils.removeRecaptchaCover()
      }, 1000)
    } else {
      this.$router.replace({
        name: 'Register',
      })
    }
  },
  methods: {
    ...mapActions(['setErrorOnInternalServerAction', 'setIsServerConnectableAction', 'setSlideActiveAction']),
    async handleDirectUserToDemoMode() {
      if (this.refer === constants.referPublicTemplate) {
        Utils.gaTrackEvent(gaCategories.CATEGORY_PUBLIC_TEMPLATE_DEMO_MODE, 'Create new presentation', 'Use public template')
        this.$store.state.isReferPublicTemplate = true
      }
      if (this.fromButton === constants.buttonScratch) {
        Utils.gaTrackEvent(gaCategories.CATEGORY_PUBLIC_TEMPLATE_LISTING, 'Create new presentation', 'Use blank public template')
      }
      try {
        if (!cookie.getValue('ahaToken')) {
          await this.handleCreateGuestAndDemoPresentation()
        } else {
          const user = await UserApi.getUser()
          await this.handleCreateDemoPresentation(user)
        }
      } catch (error) {
        if (error.status === 0) {
          this.setIsServerConnectableAction(false)
        }
      }
    },

    async handleCreateGuestAndDemoPresentation() {
      try {
        const res = await UserApi.createPresenter({
          isGuest: true,
          tokenRecaptcha: this.tokenRecaptcha,
        })
        this.$store.dispatch('setCurrentUserAction', res.user)
        Utils.setCookieToken(res.token)
        cookie.setValue('aha-guest-user', res.user && res.user.id)
        Vue.http.headers.common.Authorization = `Bearer ${res.token}`
        this.handleCreateDemoPresentation(res.user)
        Utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          'Demo mode > Create guest presenter > Succeeded',
        )
      } catch (error) {
        this.tokenRecaptcha = ''
        const errorMsg = error.body && error.body.message ? error.body.message : 'Network error'
        Utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          'Demo mode > Create guest presenter > Failed',
          errorMsg,
        )

        throw error
      }
    },
    async handleCreateDemoPresentation(user) {
      try {
        const demoPresentation =
          await PresentationApi.getDemoPresentationBySourcePresentationId(this.sourcePresentationId, { refer: this.refer })
        this.setSlideActiveAction({ index: 0 })
        Utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          'Demo mode > Create demo presentation > Succeeded',
        )
        const params = { id: demoPresentation.id }
        this.$router.push({
          name: 'PresentationDetail',
          params,
          query: {
            source: 'demo',
          },
        })
      } catch (error) {
        this.setErrorOnInternalServerAction('Failed to open demo presentation')
        const errorMsg = error.body && error.body.message ? error.body.message : 'Network error'

        Utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          'Demo mode > Create demo presentation > Failed',
          errorMsg,
        )

        if (user.isGuest) {
          this.$router.replace({ name: 'Register' })
          setTimeout(() => {
            this.setErrorOnInternalServerAction(null)
          }, 5e3)
        } else {
          this.$router.replace({ name: 'Presentations' })
        }

        throw error
      }
    },

    onRecaptchaSuccess(tokenRecaptcha) {
      this.tokenRecaptcha = tokenRecaptcha
      this.handleDirectUserToDemoMode()
    },

    onRecaptchaError() {
      // If user get the demo link from other page with the 'source' query param:
      // e.g.: A blog post has a link to the demo: https://presenter.ahaslides.com/demo?sourcePresentation=53&source=https://ahaslides.com/blogs/some-blog-post.html
      // -> redirect them to the origin: https://ahaslides.com/blogs/some-blog-post.html
      // Otherwise (manually enter the demo url, for example), redirect to the home page: https://ahaslides.com
      this.setIsServerConnectableAction(false)
      const source = Utils.getParamValue('source', window.location.href)
      if (source) {
        window.location.href = source
      } else {
        window.location.href = 'https://ahaslides.com'
      }
    },

    onOffline() {
      this.setIsServerConnectableAction(false)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variable.scss";

.loading-screen {
  background-color: #fff;
  max-height: -webkit-fill-available; // fix height in ios safari (with address bar)
  @apply fixed top-0 left-0 w-full h-full z-50 flex content-center justify-center items-center;
}
</style>
